"use strict";

/**
 * Each field can have:
 * - type: "multipleSelect" | "dateRange" | "minMax" | "measurementMinMax"
 * - label: Display label for the filter
 * - displayName: Short name for display in summaries/pills
 * - placeholder: Placeholder text for select inputs
 * - options: Array or function returning filter options
 * - key: (optional) Overrides the field name when mapping to backend data
 * - spatialGroup: (optional) Indicates spatial/geographic section
 * - hideIfNoDataKey: (optional) Hides filter if no data exists for the specified key
 * - step/minAttribute/maxAttribute: (optional) For numeric range filters
 * - unitOptions: (optional) For measurement filters with unit conversion
 */
const FilterFieldConstants = function () {
  const getFieldDefinitions = function () {
    const ToolSettings = require("../settings/toolSettings");
    const FilterConstants = require("../filterConstants");
    const Filters = require("../mapFunctions/filters");
    const BmpFcsInventoryConstants = require("../bmpfcs/bmpFcsInventoryConstants");
    const IncidentInvestigationConstants = require("../illicitDischarge/incidentInvestigation/incidentInvestigationConstants");
    const WqMonitoringConstants = require("../wq/inspection/wqMonitoringConstants");
    const ActivityConstants = require("../peo/activityConstants");
    const ActivityInventoryInfo = require("../peo/activityInventoryInfo");
    const ActivityFilters = require("../peo/activityFilters");
    const IndcomInventoryConstants = require("../indcom/indcomInventoryConstants");
    const SamplingSiteInventoryConstants = require("../wq/samplingSiteInventoryConstants");

    return {
      // Spatial filter keys
      searchString: {},

      drainsToRw: {
        type: "multipleSelect",
        label: "Receiving Waters",
        displayName: "Receiving Waters",
        placeholder: "All Receiving Waters",
        spatialGroup: "catchment",
        headerGroup: "Geographic Focus",
        options: () => Filters.getReceivingWaterOptions(),
      },

      drainsToC: {
        type: "multipleSelect",
        label: "Urban Drainage",
        displayName: "Urban Drainage",
        placeholder: "All Urban Drainage Areas",
        spatialGroup: "catchment",
        headerGroup: "Geographic Focus",
        options: () => Filters.getUrbanDrainageOptions(),
      },

      catchmentName: {
        type: "multipleSelect",
        label: "Urban Catchments",
        displayName: "Urban Catchments",
        placeholder: "All Urban Catchments",
        spatialGroup: "catchment",
        headerGroup: "Geographic Focus",
        options: () => Filters.getCatchmentOptions(),
      },

      highwayName: {
        type: "multipleSelect",
        label: "Highways",
        displayName: "Highways",
        placeholder: "All Highways",
        spatialGroup: "highway",
        headerGroup: "Geographic Focus",
        options: () => Filters.getHighwayOptions(),
      },

      zones: {
        type: "multipleSelect",
        label: "Zones",
        displayName: "Zones",
        placeholder: "All Zones",
        spatialGroup: "zone",
        headerGroup: "Geographic Focus",
        options: () => Filters.getZoneOptions(),
      },

      subGroups: {
        type: "multipleSelect",
        label: "Subgroup",
        displayName: "Subgroup",
        placeholder: "All Subgroups",
        spatialGroup: true,
        headerGroup: "Geographic Focus",
        options: () => Filters.getSubGroupOptions(),
      },

      // Multiple select filters
      implementerType: {
        type: "multipleSelect",
        label: "Delivered By",
        displayName: "Delivered By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      responsiblePartyMaintenance: {
        type: "multipleSelect",
        label: "Maintained By",
        displayName: "Maintained By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      types: {
        type: "multipleSelect",
        label: "sBMP Type",
        displayName: "Type",
        placeholder: "All sBMP Types",
        options: function () {
          const bmpTypeOptions = ToolSettings.getSetting("filterConstants", "bmpTypes");

          return FilterConstants.sbmpTypes.map(function (type) {
            return bmpTypeOptions.find((option) => option.value == type);
          });
        },
      },

      fcsType: {
        key: "types",
        type: "multipleSelect",
        label: "FCS Type",
        displayName: "Type",
        placeholder: "All FCS Types",
        options: () => ToolSettings.getSetting("filterConstants", "bmpTypes"),
      },

      catchBasinType: {
        key: "types",
        type: "multipleSelect",
        label: "Catch Basin Type",
        displayName: "Type",
        placeholder: "All Catch Basin Types",
        options: () =>
          ToolSettings.getSetting("filterConstants", "bmpTypes").filter((option) =>
            [11, 12].includes(option.value),
          ),
      },

      implementationPhase: {
        type: "multipleSelect",
        label: "Implementation Phase",
        displayName: "Phase",
        placeholder: "All Implementation Phases",
        options: FilterConstants.muniImplementationPhaseOptions,
      },

      condition: {
        type: "multipleSelect",
        label: "Condition",
        displayName: "Condition",
        placeholder: "All Conditions",
        options: FilterConstants.conditionOptions,
        hideIfNoDataKey: "condition",
      },

      consequenceOfFailure: {
        type: "multipleSelect",
        label: "Consequence of Failure",
        displayName: "CoF",
        placeholder: "All Consequences of Failure",
        options: () => FilterConstants.cofOptionsWithNull,
      },

      correctiveAction: {
        type: "multipleSelect",
        label: "Corrective Action",
        displayName: "Corrective",
        placeholder: "All Corrective Actions",
        options: FilterConstants.getAllCorrectiveOptions,
        hideIfNoDataKey: "correctiveAction",
      },

      enforcementLevel: {
        type: "multipleSelect",
        label: "Severity Level",
        displayName: "Severity",
        placeholder: "All Severity Levels",
        options: FilterConstants.getEnforcementLevel,
        hideIfNoDataKey: "enforcementLevel",
      },

      fcs: {
        type: "multipleSelect",
        label: "Full Capture System",
        displayName: "FCS",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "fcs",
      },

      inProximityTo303dWaterbody: {
        type: "multipleSelect",
        label: "Near 303(d)",
        displayName: "Near 303(d)",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "inProximityTo303dWaterbody",
      },

      // Date range filters
      routineDate: {
        type: "dateRange",
        label: "Inspected Date",
        displayName: "Inspected",
        hideIfNoDataKey: "routine",
      },

      repairDate: {
        type: "dateRange",
        label: "Repaired Date",
        displayName: "Repaired",
        hideIfNoDataKey: "repair",
      },

      installDate: {
        type: "dateRange",
        label: "Installed Date",
        displayName: "Installed",
      },

      startDate: {
        key: "installDate",
        type: "dateRange",
        label: "Start Date",
        displayName: "Start",
      },

      phase: {
        type: "multipleSelect",
        label: "Implementation Phase",
        displayName: "Phase",
        placeholder: "All Implementation Phases",
        options: () => ToolSettings.getSetting("filterConstants", "phases"),
      },

      lastDate: {
        type: "dateRange",
        label: "Last Site Visit Date",
        displayName: "Last Visit",
      },

      cleanDate: {
        type: "dateRange",
        label: "Cleaned Date",
        displayName: "Cleaned",
        hideIfNoDataKey: "cleaned",
      },

      status: {
        type: "multipleSelect",
        label: "Implementation Phase",
        displayName: "Phase",
        placeholder: "All Implementation Phases",
        options: () => ToolSettings.getSetting("filterConstants", "phases"),
      },

      performanceTier: {
        type: "multipleSelect",
        label: "Performance Tier",
        displayName: "Performance Tier",
        placeholder: "All Performance Tiers",
        options: [
          { name: "No Criteria", value: "?" },
          { name: "Exempt", value: "0" },
          { name: "Site Design", value: "1" },
          { name: "Water Quality Treatment", value: "2" },
          { name: "Runoff Retention", value: "3" },
          { name: "Peak Management", value: "4" },
        ],
        hideIfNoDataKey: "performanceTier",
      },

      lidOwnership: {
        type: "multipleSelect",
        label: "Delivered By",
        displayName: "Delivered By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      maintainerType: {
        type: "multipleSelect",
        label: "Maintained By",
        displayName: "Maintained By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      completed: {
        type: "dateRange",
        label: "Construction Completed",
        displayName: "Construction Completed",
        hideIfNoDataKey: "projectEndDate",
      },

      noticeSentDate: {
        type: "dateRange",
        label: "Maintenance Notice Sent",
        displayName: "Notice Sent Date",
      },

      initialMaintenanceNotice: {
        type: "multipleSelect",
        label: "Initial Maintenance Notice",
        displayName: "Maintenance Notice",
        placeholder: "All Projects",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "initialMaintenanceNotice",
      },

      initialRoutineInspection: {
        type: "multipleSelect",
        label: "Initial Routine Inspection",
        displayName: "Routine Maintenance",
        placeholder: "All Projects",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "initialRoutineInspection",
      },

      alternativeCompliance: {
        type: "multipleSelect",
        label: "Alternative Compliance",
        displayName: "Alternative Compliance",
        placeholder: "All Projects",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "alternativeCompliance",
      },

      openEnforcements: {
        type: "multipleSelect",
        label: "Open Enforcements",
        displayName: "Open Enforcements",
        placeholder: "All Projects",
        options: FilterConstants.yesNoOptions,
      },

      facilityOpenEnforcements: {
        key: "openEnforcements",
        type: "multipleSelect",
        label: "Open Enforcements",
        displayName: "Open Enforcements",
        placeholder: "All Projects",
        options: [
          {
            name: "Yes",
            value: "yes",
          },
          {
            name: "No",
            value: "no",
          },
        ],
      },

      // BMP-specific filters
      sbmpCategory: {
        type: "multipleSelect",
        label: "sBMP Category",
        displayName: "sBMP Category",
        placeholder: "All sBMP Categories",
        options: [
          { name: "Centralized", value: "centralized" },
          { name: "Decentralized", value: "decentralized" },
          { name: "Unknown", value: "unknown" },
        ],
      },

      systemAccess: {
        type: "multipleSelect",
        label: "System Accessibility",
        displayName: "Accessibility",
        placeholder: "All sBMPs",
        options: [
          { name: "Confined Space (Observable)", value: "confinedSpaceObservable" },
          { name: "Confined Space (Not Observable)", value: "confinedSpaceNotObservable" },
          { name: "Open Air", value: "openAir" },
          { name: "No Data", value: "null" },
        ],
      },

      assessmentDate: {
        type: "dateRange",
        label: "Assessment Date",
        displayName: "Assessment",
        hideIfNoDataKey: "assessmentDates",
      },

      score: {
        type: "minMax",
        label: "Score",
        displayName: "Score",
        step: "0.1",
        minAttribute: "0.0",
        maxAttribute: "5.0",
        hideIfNoDataKey: "score",
      },

      bmpCondition: {
        type: "multipleSelect",
        label: "Condition",
        displayName: "Condition",
        placeholder: "All sBMPs",
        options: [
          { name: "Optimal", value: "optimal" },
          { name: "Good", value: "acceptable" },
          { name: "Fair", value: "fair" },
          { name: "Poor", value: "poor" },
          { name: "No Inspection", value: "no-inspection" },
        ],
        hideIfNoDataKey: "score",
      },

      bmpFlow: {
        type: "multipleSelect",
        label: "Low Flow Bypass",
        displayName: "Low Flow Bypass",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "bmpFlow",
      },

      drainsout: {
        type: "multipleSelect",
        label: "Capture & Reuse",
        displayName: "Capture & Reuse",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "drainsout",
      },

      diversion: {
        type: "multipleSelect",
        label: "Dry Weather Diversion",
        displayName: "Dry Weather Diversion",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "diversion",
      },

      ownership: {
        type: "multipleSelect",
        label: "Responsible Party",
        displayName: "Responsible Party",
        placeholder: "All Responsible Parties",
        options: BmpFcsInventoryConstants.responsiblePartyDeliveryOptions,
      },

      projectAssociation: {
        type: "multipleSelect",
        label: "Project Association",
        displayName: "Project",
        placeholder: "All Project Associations",
        options: FilterConstants.yesNoOptions,
      },

      // Industrial/Commercial facility filters
      facilityType: {
        type: "multipleSelect",
        label: "Facility Type",
        displayName: "Type",
        placeholder: "All Facility Types",
        options: () => {
          const facilityTypeOptions = IndcomInventoryConstants.getFacilityTypeOptions().slice();
          const noDataOption = facilityTypeOptions.find((option) => option.name === "No Data");
          if (!noDataOption) {
            facilityTypeOptions.push({ name: "No Data", value: "null" });
          }
          return facilityTypeOptions;
        },
      },

      facilityPhase: {
        type: "multipleSelect",
        label: "Phase",
        displayName: "Phase",
        placeholder: "All Phases",
        options: [
          { name: "Open", value: "open" },
          { name: "Closed", value: "closed" },
        ],
      },

      inspection: {
        type: "dateRange",
        label: "Inspected Date",
        displayName: "Inspected",
        hideIfNoDataKey: "hasInspections",
      },

      openFindings: {
        type: "multipleSelect",
        label: "Open Findings",
        displayName: "Open Findings",
        placeholder: "All Open Findings",
        options: [
          {
            name: "Yes",
            value: "yes",
          },
          {
            name: "No",
            value: "no",
          },
        ],
      },

      responsiblePartyManagement: {
        type: "multipleSelect",
        label: "Responsible Party",
        displayName: "Responsible Party",
        placeholder: "All Responsible Parties",
        options: () => {
          const responsiblePartyManagementOptions =
            IndcomInventoryConstants.responsiblePartyOptions.slice();
          const noDataOption = responsiblePartyManagementOptions.find(
            (option) => option.name === "No Data",
          );
          if (!noDataOption) {
            responsiblePartyManagementOptions.push({ name: "No Data", value: "null" });
          }
          return responsiblePartyManagementOptions;
        },
      },

      fogParticipant: {
        type: "multipleSelect",
        label: "FOG Participant",
        displayName: "FOG",
        placeholder: "All FOG Participants",
        options: FilterConstants.yesNoOptions,
      },

      isIndustrialGeneralPermit: {
        type: "multipleSelect",
        label: "Industrial General Permit",
        displayName: "IGP",
        placeholder: "All Industrial General Permit",
        options: FilterConstants.yesNoOptions,
      },

      mobileBusiness: {
        type: "multipleSelect",
        label: "Mobile Business",
        displayName: "Mobile",
        placeholder: "All Mobile Businesses",
        options: FilterConstants.yesNoOptions,
      },

      riskProfile: {
        type: "multipleSelect",
        label: "Risk Profile",
        displayName: "Risk",
        placeholder: "All Risk Profiles",
        options: () => {
          const riskProfileOptions = IndcomInventoryConstants.getRiskProfileOptions().slice();
          const noDataOption = riskProfileOptions.find((option) => option.name === "No Data");
          if (!noDataOption) {
            riskProfileOptions.push({ name: "No Data", value: "null" });
          }
          return riskProfileOptions;
        },
      },

      // Construction project compliance filters
      inspectionDate: {
        type: "dateRange",
        label: "Inspection Date",
        displayName: "Inspection",
        hideIfNoDataKey: "lastInspectionDate",
      },

      completedDate: {
        type: "dateRange",
        label: "Completed Date",
        displayName: "Completed",
      },

      constructionOwnership: {
        key: "ownership",
        type: "multipleSelect",
        label: "Delivered By",
        displayName: "Delivered By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      priority: {
        type: "multipleSelect",
        label: "Priority",
        displayName: "Priority",
        placeholder: "All Projects",
        options: FilterConstants.yesNoOptions,
      },

      paused: {
        type: "multipleSelect",
        label: "Paused Projects",
        displayName: "Paused Projects",
        placeholder: "All Projects",
        options: [
          { name: "Yes", value: true },
          { name: "No", value: false },
        ],
      },

      // Construction project delivery filters
      landOwnership: {
        type: "multipleSelect",
        label: "Land Ownership",
        displayName: "Ownership",
        placeholder: "All Land Owners",
        options: FilterConstants.responsiblePartyOptions,
        hideIfNoDataKey: "landOwnership",
      },

      associatedBmpTypes: {
        type: "multipleSelect",
        label: "Associated BMP Type",
        displayName: "BMP Type",
        placeholder: "All Associated BMP Types",
        options: () => ToolSettings.getSetting("filterConstants", "bmpTypes"),
      },

      projectType: {
        type: "multipleSelect",
        label: "Project Type",
        displayName: "Project Type",
        placeholder: "All Project Types",
        options: () => ToolSettings.getSetting("filterConstants", "projectTypeOptions"),
        hideIfNoDataKey: "projectType",
      },

      developmentType: {
        type: "multipleSelect",
        label: "Development Type",
        displayName: "Development Type",
        placeholder: "All Development Types",
        options: () => ToolSettings.getDomainOptions("development_type"),
        hideIfNoDataKey: "developmentType",
      },

      // Incidents
      activityType: {
        type: "multipleSelect",
        label: "Activity Type",
        displayName: "All Activity Types",
        placeholder: "All Activity Types",
        options: [
          { name: "Agricultural Irrigation", value: "agricultural-irrigation" },
          { name: "Concrete Wash", value: "concrete-wash" },
          { name: "Fuel/Diesel", value: "fuel-diesel" },
          { name: "Grey Water", value: "grey-water" },
          { name: "Oil", value: "oil" },
          { name: "Paint", value: "paint" },
          { name: "Pressure Washing", value: "pressure-washing" },
          { name: "Residential Irrigation", value: "residential-irrigation" },
          { name: "Water Leak", value: "water-leak" },
          { name: "Hazardous Materials", value: "hazardous-materials" },
          { name: "Sewage", value: "sewage" },
          { name: "Other", value: "other" },
          { name: "Unknown", value: "unknown" },
          { name: "No Activity", value: "no-activity" },
          { name: "No Investigations", value: "no-investigations" },
          { name: "No Data", value: "null" },
        ],
      },

      requestedDate: {
        type: "dateRange",
        label: "Requested Date",
        displayName: "Requested",
        dataView: "incidents",
      },

      incidentType: {
        type: "multipleSelect",
        label: "Incident Type",
        displayName: "Incident Type",
        placeholder: "All Incident Types",
        options: IncidentInvestigationConstants.getFilterIncidentTypeOptions,
      },

      incidentStatus: {
        type: "multipleSelect",
        label: "Incident Status",
        displayName: "Status",
        placeholder: "All Statuses",
        options: [
          { name: "New Incident", value: "new" },
          { name: "Open Investigation", value: "open" },
          { name: "Closed Investigation", value: "closed" },
        ],
      },

      closeOutReason: {
        type: "multipleSelect",
        label: "Close Out Reason",
        displayName: "Close Out Reason",
        placeholder: "All Close Out Reasons",
        options: IncidentInvestigationConstants.getFilterCloseOutReasonOptions,
      },

      // Activities
      audienceType: {
        type: "multipleSelect",
        label: "Audience Type",
        displayName: "Audience",
        placeholder: "All Audience Types",
        options: () => ActivityConstants.get().audienceTypes,
      },

      outreachType: {
        type: "multipleSelect",
        label: "Outreach Type",
        displayName: "Outreach",
        placeholder: "All Outreach Types",
        options: () =>
          ActivityFilters.getOutreachTypeOptions(
            ActivityConstants.get().activities,
          ).getFormOptions(),
      },

      activityGroup: {
        type: "multipleSelect",
        label: "Activity Group",
        displayName: "Activity Group",
        placeholder: "All Activity Groups",
        options: () =>
          ActivityFilters.getActivityGroupFilterOptions(
            ActivityConstants.get().activities,
          ).getFormOptions(),
      },

      activity: {
        type: "multipleSelect",
        label: "Activity",
        displayName: "Activity",
        placeholder: "All Activities",
        options: () => ActivityFilters.getActivityFilterOptions(ActivityConstants.get().activities),
      },

      permits: {
        type: "multipleSelect",
        label: "Permit Section",
        displayName: "Permit Section",
        placeholder: "All Permit Sections",
        options: () => ActivityConstants.get().permits,
      },

      date: {
        type: "dateRange",
        label: "Activity Date",
        displayName: "Activity",
      },

      activityAudience: {
        type: "multipleSelect",
        label: "Target Audience",
        displayName: "Target Audience",
        placeholder: "All Target Audiences",
        options: () => ActivityInventoryInfo.mapTargetFields(ActivityConstants.get().audience),
      },

      activityStaff: {
        type: "multipleSelect",
        label: "Target Staff",
        displayName: "Target Staff",
        placeholder: "All Target Staff",
        options: () => ActivityInventoryInfo.mapTargetFields(ActivityConstants.get().staff),
      },

      activityPollutant: {
        type: "multipleSelect",
        label: "Target Pollutant",
        displayName: "Pollutants",
        placeholder: "All Target Pollutants",
        options: () => ActivityInventoryInfo.mapTargetFields(ActivityConstants.get().pollutant),
      },

      activityBehavior: {
        type: "multipleSelect",
        label: "Target Behavior",
        displayName: "Behaviors",
        placeholder: "All Target Behaviors",
        options: () => ActivityInventoryInfo.mapTargetFields(ActivityConstants.get().behavior),
      },

      attendeeCount: {
        type: "minMax",
        label: "Attendee Count",
        displayName: "Attendees",
        step: "1",
        minAttribute: "0",
      },

      impressionCount: {
        type: "minMax",
        label: "Impression Count",
        displayName: "Impressions",
        step: "1",
        minAttribute: "0",
      },

      volunteerCount: {
        type: "minMax",
        label: "Volunteer Count",
        displayName: "Volunteers",
        step: "1",
        minAttribute: "0",
      },

      // Scenarios
      scenario: {
        type: "multipleSelect",
        label: "Scenario",
        displayName: "Scenario",
        placeholder: "All Scenarios",
        options: [
          { name: "Current", value: "current" },
          { name: "Potential", value: "potential" },
          { name: "No Scenario", value: "null" },
        ],
      },

      analysisReady: {
        type: "multipleSelect",
        label: "Analysis Ready",
        displayName: "Analysis Ready",
        placeholder: "All sBMPs",
        options: FilterConstants.yesNoOptions,
      },

      // Asset Management
      attributionStatus: {
        type: "multipleSelect",
        label: "Attribution Status",
        displayName: "Attribution Status",
        placeholder: "All Assets",
        options: [
          { name: "Complete", value: "true" },
          { name: "Incomplete", value: "false" },
        ],
      },

      material: {
        type: "multipleSelect",
        label: "Material",
        displayName: "Material",
        placeholder: "All Materials",
        options: [
          { name: "Complete", value: "complete" },
          { name: "Incomplete", value: "incomplete" },
          { name: "Not Applicable", value: null },
        ],
      },

      size: {
        type: "multipleSelect",
        label: "Size",
        displayName: "Size",
        placeholder: "All Sizes",
        options: [
          { name: "Complete", value: "complete" },
          { name: "Incomplete", value: "incomplete" },
          { name: "Not Applicable", value: null },
        ],
      },

      probabilityOfFailure: {
        type: "multipleSelect",
        label: "Probability of Failure",
        displayName: "POF",
        placeholder: "All Probabilities of Failure",
        options: () => ToolSettings.getDomainOptions("consequence_of_failure"),
      },

      criticality: {
        type: "multipleSelect",
        label: "Criticality",
        displayName: "Criticality",
        placeholder: "All Criticalities",
        options: () => ToolSettings.getDomainOptions("consequence_of_failure"),
      },

      installDateNoDate: {
        key: "installDate",
        type: "dateRange",
        label: "Start Date",
        displayName: "Start",
        noDateCheckbox: true,
      },

      // Dry Weather
      flowCondition: {
        type: "multipleSelect",
        label: "Flow Condition",
        displayName: "Flow Conditions",
        placeholder: "All Flow Conditions",
        options: WqMonitoringConstants.getFilterFlowConditionOptions,
      },

      collectSamples: {
        type: "multipleSelect",
        label: "Sample Collected?",
        displayName: "Sample",
        placeholder: "All Locations",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "collectSamples",
      },

      illicitDischarge: {
        type: "multipleSelect",
        label: "Illicit Discharge?",
        displayName: "Discharge",
        placeholder: "All Locations",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "illicitDischarge",
      },

      waterQualityExceedances: {
        type: "multipleSelect",
        label: "Water Quality Exceedence?",
        displayName: "Exceedance",
        placeholder: "All Locations",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "waterQualityExceedances",
      },

      iddeScreeningDate: {
        type: "dateRange",
        label: "Dry Weather Screening Date",
        displayName: "Dry Weather Screening",
        dataView: "dry-weather",
      },

      cof: {
        type: "multipleSelect",
        label: "Consequence of Failure",
        displayName: "CoF",
        placeholder: "All Consequences of Failure",
        options: FilterConstants.cofOptionsWithNull,
      },

      assetType: {
        type: "multipleSelect",
        label: "Asset Type",
        displayName: "Asset Type",
        placeholder: "All Asset Types",
        options: () => {
          const assetDisplayNames = ToolSettings.getSetting("constants", "assetDisplayNames");
          return [
            { name: assetDisplayNames["outfall"], value: "outfall" },
            { name: assetDisplayNames["catch-basin"], value: "bmpFcs" },
            { name: assetDisplayNames["manhole"], value: "manhole" },
          ];
        },
      },

      isMonitoringLocation: {
        type: "multipleSelect",
        label: "Monitoring Location",
        displayName: "Monitoring Location",
        placeholder: "All Locations",
        options: FilterConstants.yesNoOptions,
      },

      // VA (Trash Assessment)
      vaCondition: {
        key: "condition",
        type: "multipleSelect",
        label: "Trash Condition",
        displayName: "Trash Condition",
        placeholder: "All Trash Conditions",
        options: [
          { name: "Low", value: "Low" },
          { name: "Moderate", value: "Moderate" },
          { name: "High", value: "High" },
          { name: "Very High", value: "Very High" },
        ],
      },

      photos: {
        type: "multipleSelect",
        label: "Assessments with Photos",
        displayName: "Photos",
        placeholder: "All Assessments",
        options: [
          {
            name: "Yes",
            value: true,
          },
          {
            name: "No",
            value: false,
          },
        ],
      },

      // Sampling Site / Manholes
      manholeType: {
        type: "multipleSelect",
        label: "Manhole Type",
        displayName: "Manhole Type",
        placeholder: "All Manhole Types",
        options: () => ToolSettings.getSetting("filterConstants", "manholeTypeTypeOptions"),
      },

      sitePhase: {
        type: "multipleSelect",
        label: "Site Phase",
        displayName: "Site Phase",
        placeholder: "All Site Phases",
        options: SamplingSiteInventoryConstants.getSitePhaseOptions,
      },

      siteType: {
        type: "multipleSelect",
        label: "Site Type",
        displayName: "Site Type",
        placeholder: "All Site Types",
        options: SamplingSiteInventoryConstants.getSiteTypeOptions,
      },

      shape: {
        type: "multipleSelect",
        label: "Shape",
        displayName: "Shape",
        placeholder: "All Shapes",
        options: FilterConstants.shapeOptionsCleanoutManholes,
        hideIfNoDataKey: "shape",
      },

      linedAssetsShapes: {
        key: "shape",
        type: "multipleSelect",
        label: "Shape",
        displayName: "Shape",
        placeholder: "All Shapes",
        options: FilterConstants.linedAssetsShapes,
        hideIfNoDataKey: "shape",
      },

      outletShape: {
        key: "shape",
        type: "multipleSelect",
        label: "Shape",
        displayName: "Shape",
        placeholder: "All Shapes",
        options: FilterConstants.outfallShapeOptions,
        hideIfNoDataKey: "outletShape",
      },

      diameterRange: {
        type: "measurementMinMax",
        label: "Diameter",
        displayName: "Diameter",
        step: "0.1",
        minAttribute: "0.0",
        maxAttribute: "999999.0",
        unitOptions: ["ft", "in"],
      },

      lengthRange: {
        type: "measurementMinMax",
        label: "Length",
        displayName: "Length",
        step: "0.1",
        minAttribute: "0.0",
        maxAttribute: "999999.0",
        unitOptions: ["ft", "in"],
      },

      widthRange: {
        type: "measurementMinMax",
        label: "Width",
        displayName: "Width",
        step: "0.1",
        minAttribute: "0.0",
        maxAttribute: "999999.0",
        unitOptions: ["ft", "in"],
      },

      // Additional muni infrastructure filters
      maintainedBy: {
        type: "multipleSelect",
        label: "Maintained By",
        displayName: "Maintained By",
        placeholder: "All Responsible Parties",
        options: FilterConstants.responsiblePartyOptions,
      },

      liftStationType: {
        type: "multipleSelect",
        label: "Lift Station Type",
        displayName: "Lift Station Type",
        placeholder: "All Lift Station Types",
        options: FilterConstants.liftStationTypes,
      },

      cleanoutType: {
        type: "multipleSelect",
        label: "Cleanout Type",
        displayName: "Cleanout Type",
        placeholder: "All Cleanout Types",
        options: FilterConstants.cleanoutType,
      },

      culvertType: {
        type: "multipleSelect",
        label: "Culvert Type",
        displayName: "Culvert Types",
        placeholder: "All Culvert Types",
        options: FilterConstants.culvertType,
      },

      serviceType: {
        type: "multipleSelect",
        label: "Service Type",
        displayName: "Service Type",
        placeholder: "All Service Types",
        options: FilterConstants.serviceType,
        hideIfNoDataKey: "serviceType",
      },

      accessible: {
        type: "multipleSelect",
        label: "Accessible",
        displayName: "Accessible",
        placeholder: "All Accessibilities",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "accessible",
      },

      lined: {
        type: "multipleSelect",
        label: "Lined",
        displayName: "Lined",
        placeholder: "All Lined Attributes",
        options: FilterConstants.yesNoOptions,
        hideIfNoDataKey: "lined",
      },

      openChannelType: {
        type: "multipleSelect",
        label: "Open Channel Type",
        displayName: "Open Channel Type",
        placeholder: "All Open Channel Types",
        options: FilterConstants.openChannelType,
      },

      flowType: {
        type: "multipleSelect",
        label: "Flow Type",
        displayName: "Flow Type",
        placeholder: "All Flow Types",
        options: FilterConstants.flowType,
        hideIfNoDataKey: "flowType",
      },

      pipeType: {
        key: "type",
        type: "multipleSelect",
        label: "Pipe Type",
        displayName: "Pipe Type",
        placeholder: "All Pipe Types",
        options: FilterConstants.pipeTypeOptions,
      },

      outfallType: {
        type: "multipleSelect",
        label: "Outfall Type",
        displayName: "Outfall Type",
        placeholder: "All Outfall Types",
        options: [
          { name: "Closed Pipe", value: "closed-pipe" },
          { name: "Open Channel", value: "open-channel" },
          { name: "Underground", value: "underground" },
          { name: "Unknown", value: "unknown" },
          { name: "Other", value: "other" },
          { name: "No Data", value: "null" },
        ],
      },

      outletType: {
        type: "multipleSelect",
        label: "Outlet Type",
        displayName: "Outlet Type",
        placeholder: "All Outlets Types",
        options: [
          { name: "Apron", value: "apron" },
          { name: "Ditch Outlet", value: "ditch-outlet" },
          { name: "Earthen", value: "earthen" },
          { name: "Endwall", value: "endwall" },
          { name: "Flap Gate", value: "flap-gate" },
          { name: "Flared Endsection", value: "flared" },
          { name: "Pipe", value: "pipe" },
          { name: "Riser Structure", value: "riser-structure" },
          { name: "Rundown", value: "rundown" },
          { name: "Slotted", value: "slotted" },
          { name: "Tide Chamber", value: "tide-chamber" },
          { name: "Weir", value: "weir" },
          { name: "Other", value: "other" },
          { name: "Unknown", value: "unknown" },
          { name: "No Data", value: "null" },
        ],
        hideIfNoDataKey: "outletType",
      },

      // To dos
      assignee: {
        type: "multipleSelect",
        label: "Assignee",
        displayName: "Assignee",
        placeholder: "All Assignees",
        options: () => {
          const options = ToolSettings.getSetting("group", "users")
            .sort((a, b) => {
              const aName = a.fullName?.toLowerCase() || "";
              const bName = b.fullName?.toLowerCase() || "";
              return aName.localeCompare(bName);
            })
            .map((user) => ({ value: user.id, name: user.fullName }));
          options.push({ value: "null", name: "Unassigned" });
          return options;
        },
      },

      dueDate: {
        type: "dateRange",
        label: "Due Date",
        displayName: "Due Date",
      },
    };
  };

  const getFieldDefinition = function (key) {
    const definition = getFieldDefinitions()[key];

    if ("options" in definition) {
      definition.options =
        typeof definition.options === "function" ? definition.options() : definition.options;
      definition.options = definition.options.map((o) => ({ ...o, value: String(o.value) }));
    }

    return definition;
  };

  const getToDoFilterFields = function () {
    return [...getSpatialFilterKeys(), "assignee", "dueDate"];
  };

  const getSpatialFilterKeys = function () {
    return ["highwayName", "zones", "drainsToRw", "drainsToC", "catchmentName", "subGroups"];
  };

  return {
    getFieldDefinitions,
    getFieldDefinition,
    getToDoFilterFields,
    getSpatialFilterKeys,
  };
};

module.exports = FilterFieldConstants();
